import { subscriptionState, userState } from "src/state/state";
import type {
  SignupCustomCampaign,
  SignupCustomStep
} from "src/ui/components/SignupCustomContent/SignupCustom.types";
import {
  SignupCustomCampaignName,
  SignupCustomStepType
} from "src/ui/components/SignupCustomContent/SignupCustom.types";

import FunnelKey from "src/constants/funnelKey";

import {
  GetSuggestedTreatmentPlanResponse,
  SubscriptionDetailsResponse
} from "@9amhealth/openapi";
import {
  CDCDPP_ELIGIBILITY_QUESTIONNAIRE_ID,
  SUBSCRIPTION_PLAN_ID_CDCDPP
} from "src/constants/misc";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import ShipmentAddressCubit from "src/state/ShipmentAddressCubit/ShipmentAddressCubit";
import { StorageController } from "src/state/StorageBloc/StorageBloc";
import PersonalizedWelcomeTitle from "src/ui/components/PersonalizedWelcomeTitle/PersonalizedWelcomeTitle";
import CDCDPPCustomizedCheckoutScreen from "src/ui/components/SignupCustomContent/campaigns/cdcdpp/CDCDPPCustomizedCheckoutScreen";
import HealthInformationExchangeStep from "src/ui/components/SignupCustomContent/HealthInformationExchangeStep";
import SignupCustomIneligibility, {
  IneligibleIcon
} from "src/ui/components/SignupCustomContent/SignupCustomIneligibility";
import Translate from "src/ui/components/Translate/Translate";
import { SignupCustomStepCheckoutRawContent } from "../steps/SignupCustomStepCheckoutCustomRender";
import { Blac } from "blac-next";
import UserPreferencesCubit from "src/state/UserPreferencesCubit/UserPreferencesCubit";

export enum CDC_DPP_FunnelLocalStorageKeys {
  EligibilityCheckCompleted = "9am_cdcdpp_eligibility-check-completed",
  ShippingStepCompleted = "9am_cdcdpp_shipping-step-completed"
}

export enum SignupCustomCampaignCDCDPPSteps {
  healthInformationExchange = "health-information-exchange",
  register = "register",
  eligibility = "eligibility",
  shipping = "shipping",
  checkout = "checkout",
  success = "success"
}

export class SignupCustomCampaignCDCDPP {
  private readonly campaign: SignupCustomCampaign;
  name: SignupCustomCampaignName = SignupCustomCampaignName.CDC_DPP;
  stepOrder: SignupCustomCampaignCDCDPPSteps[] = [
    SignupCustomCampaignCDCDPPSteps.register,
    SignupCustomCampaignCDCDPPSteps.eligibility,
    SignupCustomCampaignCDCDPPSteps.shipping,
    SignupCustomCampaignCDCDPPSteps.checkout,
    SignupCustomCampaignCDCDPPSteps.success
  ];

  constructor() {
    this.campaign = {
      name: this.name,
      logo: "https://cdn.sanity.io/images/g38rxyoc/production/8560aac996774505f5deee72c2611673a1907ce2-96x56.png",
      prettyName: "CDCDPP",
      funnelKey: FunnelKey.cdc_dpp,
      steps: this.generateStepList
    };
    this.populateDefaultSteps();
  }

  get(): SignupCustomCampaign {
    return this.campaign;
  }

  addedSteps: SignupCustomStep[] = [];
  generateStepList = (): SignupCustomStep[] => {
    this.refreshSteps();
    return this.addedSteps;
  };

  addStep(step: SignupCustomStep): typeof this {
    this.addedSteps.push(step);
    return this;
  }

  refreshSteps = (): void => {
    this.addedSteps = [];
    this.populateDefaultSteps();
  };

  hasShippingAddress = false;
  checkUserHasShippingAddress = async (): Promise<boolean> => {
    if (this.hasShippingAddress) {
      return true;
    }

    try {
      const userShippingAddressResponse =
        await ShipmentAddressCubit.loadShipmentAddress();
      const { street, zip, city, state } =
        userShippingAddressResponse.data.addressData;
      const userAddress =
        street?.trim() && zip?.trim() && city?.trim() && state?.trim();
      if (userAddress) {
        this.hasShippingAddress = true;
        return true;
      }
    } catch (e) {
      reportErrorSentry(e);
    }

    return false;
  };

  addStepsMap: Record<SignupCustomCampaignCDCDPPSteps, () => void> = {
    [SignupCustomCampaignCDCDPPSteps.healthInformationExchange]: () => {
      this.addStep(this.createHealthInformationExchangeStep());
    },
    [SignupCustomCampaignCDCDPPSteps.register]: () => {
      this.addStep(this.createAccountStep());
    },
    [SignupCustomCampaignCDCDPPSteps.eligibility]: () => {
      const eligibilityStep = this.createEligibilityStep();
      const ineligibilityStep = this.createIneligibilityStep(
        eligibilityStep,
        "ineligible"
      );
      this.addStep(eligibilityStep);
      this.addStep(ineligibilityStep);
    },
    [SignupCustomCampaignCDCDPPSteps.shipping]: () => {
      this.addStep(this.createShippingStep());
    },
    [SignupCustomCampaignCDCDPPSteps.checkout]: () => {
      this.addStep(this.createCheckoutStep());
    },
    [SignupCustomCampaignCDCDPPSteps.success]: () => {
      this.addStep(this.createSuccessStep());
    }
  };

  populateDefaultSteps(): typeof this {
    for (const step of this.stepOrder) {
      this.addStepsMap[step]();
    }
    return this;
  }

  createHealthInformationExchangeStep(): SignupCustomStep {
    return {
      name: translate("healthInfoExchange"),
      path: "health-information-exchange",
      type: SignupCustomStepType.InfoPage,
      dataComplete: async () => {
        const userPreferences = Blac.getBloc(UserPreferencesCubit);
        return userPreferences.isHealthInformationExchangeCompleted;
      },
      render: () => {
        return <HealthInformationExchangeStep />;
      }
    };
  }

  createAccountStep(): SignupCustomStep {
    return {
      name: translate("register"),
      path: "register",
      type: SignupCustomStepType.Authentication,
      dataComplete: async () => userState.hasAppAccess()
    };
  }

  createEligibilityStep(): SignupCustomStep {
    return {
      name: translate("eligibility"),
      path: "eligibility",
      type: SignupCustomStepType.Questionnaire,
      questionnaireId: CDCDPP_ELIGIBILITY_QUESTIONNAIRE_ID,
      questionnaireName: "Signup Questionnaire",
      dataComplete: async (): Promise<boolean> => {
        const [treatment, answeredQuestionnaire] = await Promise.all([
          subscriptionState.checkTreatmentTargetEligibility(
            subscriptionState.knownTreatmentPlanTargets.CDC_DPP
          ),
          userState.checkUserHasAnsweredQuestionnaire(
            CDCDPP_ELIGIBILITY_QUESTIONNAIRE_ID
          )
        ]);

        if (
          treatment?.eligibility ===
          GetSuggestedTreatmentPlanResponse.eligibility.INELIGIBLE
        ) {
          if (!window.location.pathname.includes("/ineligible")) {
            window.location.href = "/signup/cdcdpp/ineligible";
          }
          return true;
        }

        return (
          treatment?.eligibility ===
            GetSuggestedTreatmentPlanResponse.eligibility.ELIGIBLE &&
          Boolean(answeredQuestionnaire)
        );
      },
      onNextStep: (): void => {
        StorageController.setItem(
          CDC_DPP_FunnelLocalStorageKeys.EligibilityCheckCompleted,
          "true"
        );
      }
    };
  }

  createIneligibilityStep(
    resolveStep: SignupCustomStep,
    path: string
  ): SignupCustomStep {
    return {
      name: "Ineligible",
      path: path,
      type: SignupCustomStepType.InfoPage,
      render: () => (
        <SignupCustomIneligibility
          icon={IneligibleIcon.DOCTOR}
          hideButton={false}
          buttonText={<Translate msg="ineligibility.button" />}
          resolveStep={resolveStep}
        >
          <nine-spacer s="md"></nine-spacer>
          <h4>
            <Translate msg="ineligibility.title" />
          </h4>
          <nine-spacer s="md"></nine-spacer>
          <p className="m0 color-c-80 ineligibility-description">
            <Translate msg="ineligibility.description" />
          </p>
        </SignupCustomIneligibility>
      ),
      dataComplete: async (): Promise<boolean> => {
        const [treatment] = await Promise.all([
          subscriptionState.checkTreatmentTargetEligibility(
            subscriptionState.knownTreatmentPlanTargets.CDC_DPP
          )
        ]);

        return (
          treatment?.eligibility !==
          GetSuggestedTreatmentPlanResponse.eligibility.INELIGIBLE
        );
      }
    };
  }

  createShippingStep(): SignupCustomStep {
    return {
      name: translate("shippingAddress"),
      path: "shipping",
      type: SignupCustomStepType.ShippingAddress,
      dataComplete: this.checkUserHasShippingAddress,
      shippingFormPageProps: {
        title: translate("yourDetails")
      }
    };
  }

  createCheckoutStep(): SignupCustomStep {
    return {
      name: translate("reviewCheckout"),
      path: "checkout",
      type: SignupCustomStepType.InfoPage,
      dataComplete: async (): Promise<boolean> => {
        await subscriptionState.loadAllSubscriptions();
        const funnelSpecificSubscription =
          subscriptionState.filterAllSubscriptions({
            status: [
              SubscriptionDetailsResponse.status.ACTIVE,
              SubscriptionDetailsResponse.status.IN_REVIEW
            ],
            planIds: [SUBSCRIPTION_PLAN_ID_CDCDPP],
            looseCheck: true
          })[0] as SubscriptionDetailsResponse | undefined;

        return Boolean(funnelSpecificSubscription);
      },
      render: () => (
        <SignupCustomStepCheckoutRawContent
          content={{
            title: translate("cdcdcpp.title"),
            subtitle: translate("cdcdpp.description"),
            showMoneyBackGuarantee: false,
            infoSections: () => <CDCDPPCustomizedCheckoutScreen />
          }}
          showShipping={false}
          totalPrice={0}
          totalPriceFirstMonth={0}
          labTestsIncluded={false}
          cartItems={{
            onboarding: {
              show: false,
              fee: 0
            },
            ongoingCare: {
              show: false,
              fee: 0
            }
          }}
        />
      )
    };
  }

  createSuccessStep(): SignupCustomStep {
    return {
      name: translate("success"),
      path: "checkout-success",
      type: SignupCustomStepType.SuccessPage,
      successPageProps: {
        titleSpacer: false,
        title: <PersonalizedWelcomeTitle />,
        subtitle: translate("cdcdpp.tasks")
      },
      dataComplete: async () => {
        return false;
      },
      action: (navigate) => {
        navigate("/app");
      }
    };
  }
}

const campaignInstance = new SignupCustomCampaignCDCDPP();
export const signupCustomCampaignCDCDPP = campaignInstance.get();
